'use client';
import { useState, useEffect } from 'react';
import Link from'next/link';
import { usePathname } from 'next/navigation';
import Icon from'@/components/ui/AppIcon';
import SearchBar from '@/components/common/SearchBar';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const pathname = usePathname()

  useEffect(() => {
    setMounted(true)
  }, [])

  const navLinks = [
    { id: 'nav_home', label: 'Home', href: '/' },
    { id: 'nav_about', label: 'About Us', href: '/about' },
    { id: 'nav_products', label: 'Products', href: '/products' },
    { id: 'nav_testimonials', label: 'Testimonials', href: '/testimonials' },
    { id: 'nav_blog', label: 'Blog', href: '/blog' },
    { id: 'nav_contact', label: 'Contact', href: '/contact' },
  ]

  const isActive = (href: string) => mounted && pathname === href

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="max-w-container mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 mr-8 md:mr-12 lg:mr-16">
            <div className="flex flex-col items-center gap-1 antialiased">
              <span className="text-base md:text-lg lg:text-xl font-extrabold text-orange-600 leading-tight whitespace-nowrap tracking-tight">
                Supplies Distributors SA
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm md:text-base font-bold text-orange-600 leading-tight whitespace-nowrap tracking-wide">
                  (SDSA)
                </span>
                <Icon name="CubeIcon" size={16} className="text-orange-600 flex-shrink-0" />
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                className={`text-sm font-medium transition-colors relative ${
                  isActive(link.href)
                    ? 'text-primary' :'text-foreground hover:text-primary'
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute -bottom-[21px] left-0 right-0 h-0.5 bg-primary" />
                )}
              </Link>
            ))}
          </nav>

          {/* Desktop Search & CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <div className="w-64">
              <SearchBar />
            </div>
            <Link
              href="/contact"
              className="btn-scale inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-primary/90 whitespace-nowrap"
            >
              Request Quote
              <Icon name="ArrowUpRightIcon" size={16} className="arrow-shift" />
            </Link>
          </div>

          {/* Mobile Actions */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={() => {
                setMobileSearchOpen(!mobileSearchOpen);
                setMobileMenuOpen(false);
              }}
              className="p-2 text-foreground"
              aria-label="Toggle search"
            >
              <Icon name="MagnifyingGlassIcon" size={22} />
            </button>
            <button
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
                setMobileSearchOpen(false);
              }}
              className="p-2 text-foreground"
              aria-label="Toggle mobile menu"
            >
              <Icon name={mobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {mobileSearchOpen && (
          <div className="lg:hidden pb-4">
            <SearchBar onClose={() => setMobileSearchOpen(false)} isMobile />
          </div>
        )}
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <nav className="flex flex-col py-4 px-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`text-base font-medium py-2 ${
                  isActive(link.href)
                    ? 'text-primary border-l-2 border-primary pl-3' :'text-foreground hover:text-primary'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-200 space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="PhoneIcon" size={16} />
                <span>+32 4 123 4567</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="EnvelopeIcon" size={16} />
                <span>info@suppliesdistributorssa.com</span>
              </div>
            </div>
            <Link
              href="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-semibold mt-4"
            >
              Request Quote
              <Icon name="ArrowUpRightIcon" size={16} />
            </Link>
          </nav>
        </div>
      )}
    </header>
  )
}